import { Select } from '@care/react-component-lib';
import { MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { useFilterField } from '@care/provider-react-components';
import { EventsSortBy } from '@/__generated__/globalTypes';
import { useSegmentationFiltersContext } from '@/components/contexts/SegmentationFiltersContext';
import { saveFilters } from '@/utils/filtersStorage';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';

const SortByEnumToString: Record<EventsSortBy, string> = {
  [EventsSortBy.RECOMMENDED]: 'Recommended',
  [EventsSortBy.NEWEST]: 'Newest',
};

const options = [EventsSortBy.RECOMMENDED, EventsSortBy.NEWEST];
const defaultValue = EventsSortBy.RECOMMENDED;

export const SortByDropdown = () => {
  const { filters, setFilters, fetchResults } = useSegmentationFiltersContext();

  const { value, setValue } = useFilterField({
    name: 'sortBy',
    type: 'single-select',
    label: 'Sort By',
    options,
    defaultValue,
    modalProps: { hideInModal: true },
  });

  const renderSelectValue = (currentValue: unknown) => {
    const selectedSortBy = currentValue as EventsSortBy;
    return (
      <Typography variant="body2" component="span">
        Sort by: <b>{SortByEnumToString[selectedSortBy]}</b>
      </Typography>
    );
  };

  const handleSortByChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as EventsSortBy;
    setValue(selectedValue);

    const { __typename: typename, location, ...restFilters } = filters ?? {};

    const zipcode = location?.zipcode ?? undefined;
    const updatedFilters = {
      ...restFilters,
      zipcode,
      sortBy: selectedValue,
    };

    saveFilters(updatedFilters);
    setFilters(updatedFilters);
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'Sort Applied',
      sort: SortByEnumToString[selectedValue],
    });
    fetchResults(updatedFilters, true);
  };

  const handleSelectOpen = () => {
    trackAmplitudeEvent(AmplitudeEvent.seoFeatureInteracted, {
      feature_name: 'Sort Viewed',
    });
  };

  const selectValue = value.length ? value : defaultValue;

  return (
    <Select
      data-testid="sort-by-dropdown"
      value={selectValue}
      onChange={handleSortByChange}
      onOpen={handleSelectOpen}
      renderValue={renderSelectValue}>
      <MenuItem value={EventsSortBy.RECOMMENDED}>
        {SortByEnumToString[EventsSortBy.RECOMMENDED]}
      </MenuItem>
      <MenuItem value={EventsSortBy.NEWEST}>{SortByEnumToString[EventsSortBy.NEWEST]}</MenuItem>
    </Select>
  );
};
